import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "maison_cart_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = (product, quantity = 1) => {
        setItems((prev) => {
            const existing = prev.find((i) => i.product_id === product.id);
            if (existing) {
                return prev.map((i) =>
                    i.product_id === product.id
                        ? { ...i, quantity: i.quantity + quantity }
                        : i
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    photo_url: product.photo_url,
                    quantity,
                },
            ];
        });
    };

    const updateQty = (product_id, quantity) => {
        setItems((prev) =>
            prev
                .map((i) => (i.product_id === product_id ? { ...i, quantity } : i))
                .filter((i) => i.quantity > 0)
        );
    };

    const removeItem = (product_id) => {
        setItems((prev) => prev.filter((i) => i.product_id !== product_id));
    };

    const clear = () => setItems([]);

    const totals = useMemo(() => {
        const count = items.reduce((s, i) => s + i.quantity, 0);
        const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
        return { count, subtotal: Math.round(subtotal * 100) / 100 };
    }, [items]);

    return (
        <CartContext.Provider
            value={{ items, addItem, updateQty, removeItem, clear, ...totals }}
        >
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be inside CartProvider");
    return ctx;
}
